import { type Ref, watch } from 'vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import type { Insight, InsightAxisGenerationStatus } from '@/types/insights';
import { insightPayloadAxes } from '@/utils/insights/insightAxis';

export const useInsightAxisErrorNotice = ({
  insight,
  isOpen,
  retry,
}: {
  insight: Ref<Insight | undefined>;
  isOpen: Ref<boolean>;
  retry: () => Promise<void> | void;
}) => {
  const { createSnackbar } = useSnackbar();
  /**
   * ユーザーが見ていない間（カードが閉じている間や、ページを開く前）に失敗した軸は知らせない。
   * 開いている間に error へ変わったことを見届けた軸だけを知らせるため、
   * uuid と軸の組ごとに直前の状態を覚えておく。
   */
  const lastAxisStatuses = new Map<string, InsightAxisGenerationStatus>();

  watch(
    () => {
      const value = insight.value;
      const axisStatuses = value?.payload
        ? insightPayloadAxes(value.payload).map(axis => axis.generation_status)
        : [];
      return [
        value?.uuid,
        value?.generation_status,
        axisStatuses.join(','),
        isOpen.value,
      ] as const;
    },
    () => {
      const value = insight.value;
      if (!value?.uuid || value.payload === undefined) return;
      const uuid = value.uuid;
      const newlyFailed = insightPayloadAxes(value.payload).filter(axis => {
        const key = `${uuid}:${axis.axis}`;
        const previous = lastAxisStatuses.get(key);
        lastAxisStatuses.set(key, axis.generation_status);
        return (
          previous !== undefined &&
          previous !== 'error' &&
          axis.generation_status === 'error'
        );
      });

      if (!isOpen.value || value.generation_status === 'error') return;
      if (newlyFailed.length === 0) return;

      // 文言は軸を区別しないため、同時に複数の軸が失敗していても 1 回にまとめる。
      createSnackbar({
        type: 'retry',
        message: '一部の発想のヒントの生成に失敗しました',
        description: '申し訳ありませんが、再度お試しください',
        params: { linkText: '再度試す', onClickLink: () => retry() },
      });
    },
    { immediate: true },
  );
};
